import Alert from "@mui/material/Alert";
import dayjs from "dayjs";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { PageLayout, UserLink } from "@/shared/components";
import { TagList } from "@/shared/components/TagList";
import { DatasetRecord } from "@/shared/domain/datasets/DatasetRecord";
import {
  datasetQueryKeys,
  useDatasetMetadataKeys,
} from "@/shared/domain/datasets/hooks";
import {
  computeMetadataRows,
  formatMetadataValue,
  projectRows,
  QueryContentMode,
  QueryTarget,
  type EntityIdGetter,
  type MetadataKey,
  type Row,
} from "@/shared/domain/query";
import {
  useMetadataValues,
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useDatasetTablePreferences } from "@/shared/domain/users/hooks";
import {
  DataTable,
  buildColumnsForPreferences,
  colId,
  createColumnHelper,
  filterToCondition,
  useDataTableState,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type FilterDefinition,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation/Goto";
import { RobotoPrincipal, RobotoPrincipalType } from "@/shared/principal";
import type { SearchQueryBody, SortDirection } from "@/types";

import styles from "./DatasetsList.module.css";

type DatasetRow = Row<DatasetRecord>;

const getDatasetId: EntityIdGetter<DatasetRecord> = (d) => d.dataset_id;

const columnHelper = createColumnHelper<DatasetRow>();

const STATIC_DATASET_COLUMNS: ColDef<DatasetRow>[] = [
  columnHelper.accessor("dataset_id", {
    headerName: "ID",
    width: 150,
    cellClass: styles.monoCell,
    cellRenderer: (p: ICellRendererParams<DatasetRow, string>) => {
      const id = p.value;
      if (!id) {
        return null;
      }
      return (
        <Link to={`${Goto.route.Datasets}/${id}`} className={styles.nameLink}>
          {id}
        </Link>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    sortable: true,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    width: 150,
    sortable: true,
    cellRenderer: CreatedByCell,
  }),
  columnHelper.accessor("modified", {
    headerName: "Modified",
    width: 150,
    sortable: true,
    valueFormatter: (p) => {
      if (!p.value) {
        return "";
      }
      const value = dayjs(p.value);
      return dayjs().diff(value, "day") >= 7
        ? value.format("MMM D, YYYY")
        : value.fromNow();
    },
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    width: 150,
    sortable: true,
    cellRenderer: CreatedByCell,
  }),
  columnHelper.accessor("org_id", {
    headerName: "Org",
    width: 150,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    cellStyle: {
      display: "flex",
      alignItems: "center",
    },
    cellRenderer: (p: ICellRendererParams<DatasetRow, string[]>) => (
      <TagList tags={p.value ?? []} maxDisplayCount={3} />
    ),
  }),
];

function buildMetadataColumn(metadataKey: string): ColDef<DatasetRow> {
  const id: MetadataKey = `metadata.${metadataKey}`;
  return {
    colId: id,
    headerName: metadataKey,
    valueGetter: (p) => p.data?.[id],
    valueFormatter: (p) => formatMetadataValue(p.value),
    width: 160,
    // Dataset metadata is never sortable
    sortable: false,
  };
}

const DEFAULT_VISIBLE_IDS = STATIC_DATASET_COLUMNS.map(colId);

const STATIC_PICKER_OPTIONS: ColumnPickerOption[] = STATIC_DATASET_COLUMNS.map(
  (c) => ({
    id: colId(c),
    label: c.headerName ?? colId(c),
  }),
);

export function DatasetsList() {
  const { currentOrganization } = useAuth();
  const { datasetService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useDatasetTablePreferences();

  const metadataKeysQuery = useDatasetMetadataKeys(resolvedOrgId);

  // Capture initial columns preferences once at mount.
  const [initialColumnIds] = React.useState(() => preferences.columns);

  // TODO(custom-fields): concat another `dynamicColumns` source when the
  // backend / frontend hook lands.
  const columns = React.useMemo<ColDef<DatasetRow>[]>(
    () =>
      buildColumnsForPreferences<DatasetRow>({
        staticColumns: STATIC_DATASET_COLUMNS,
        dynamicColumns: (metadataKeysQuery.data ?? []).map(buildMetadataColumn),
        preferenceOrder: initialColumnIds,
      }),
    [initialColumnIds, metadataKeysQuery.data],
  );

  const availableColumns = React.useMemo<ColumnPickerOption[]>(
    () => [
      ...STATIC_PICKER_OPTIONS,
      ...(metadataKeysQuery.data ?? []).map<ColumnPickerOption>((key) => ({
        id: `metadata.${key}`,
        label: key,
      })),
    ],
    [metadataKeysQuery.data],
  );

  const tableState = useDataTableState<DatasetRow>({
    getRowId: getDatasetId,
    initialSort: { columnId: "created", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_IDS,
  });

  const filterDefinitions: FilterDefinition[] = React.useMemo(
    () => [
      { type: "string", field: "dataset_id", name: "Dataset Id" },
      { type: "string", field: "name", name: "Name" },
      { type: "string", field: "description", name: "Description" },
      { type: "date", field: "created", name: "Created" },
      { type: "string", field: "created_by", name: "Created By" },
      { type: "date", field: "modified", name: "Modified" },
      { type: "string", field: "modified_by", name: "Modified By" },
      {
        type: "enum",
        field: "tags",
        name: "Tags",
        options: function getDatasetsTags() {
          return datasetService.getTagsForOrg({
            resourceOwnerId: resolvedOrgId ?? "",
          });
        },
      },
    ],
    [datasetService, resolvedOrgId],
  );

  const filterCondition = filterToCondition(tableState.filters, {
    matchMode: tableState.matchMode,
  });

  const query: SearchQueryBody = {
    ...(tableState.sort && {
      sort_by: tableState.sort.columnId,
      sort_direction: tableState.sort.direction.toUpperCase() as SortDirection,
    }),
    ...(filterCondition && { condition: filterCondition }),
  };

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Datasets,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: datasetQueryKeys.list(resolvedOrgId, query),
  });

  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<DatasetRecord>(
    queryId,
    tableState.currentPage,
    tableState.pageSize,
  );

  const countQuery = useQueryCount(queryId);

  const datasets: DatasetRecord[] = React.useMemo(
    () => pageQuery.data ?? [],
    [pageQuery.data],
  );

  const visibleMetadataKeys = React.useMemo<MetadataKey[]>(() => {
    const out: MetadataKey[] = [];
    for (const id of tableState.visibleColumnIds) {
      if (id.startsWith("metadata.")) {
        out.push(id as MetadataKey);
      }
    }
    return out;
  }, [tableState.visibleColumnIds]);

  const { getMetadataValue } = useMetadataValues({
    target: QueryTarget.Datasets,
    metadataColumns: visibleMetadataKeys,
    entityIds: datasets.map(getDatasetId),
  });

  const metadataByDataset = React.useMemo(
    () =>
      computeMetadataRows(
        datasets,
        visibleMetadataKeys,
        getMetadataValue,
        getDatasetId,
      ),
    [datasets, visibleMetadataKeys, getMetadataValue],
  );

  const rows = React.useMemo<DatasetRow[]>(
    () => projectRows(datasets, metadataByDataset, getDatasetId),
    [datasets, metadataByDataset],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  return (
    <PageLayout title="Roboto - Datasets" headerContentLeft="Datasets">
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      <DataTable.Root>
        <DataTable.Actions>
          <DataTable.Filters
            filterDefinitions={filterDefinitions}
            onFilterSelected={tableState.addFilter}
          />
          <DataTable.ColumnVisibility
            options={availableColumns}
            visibleColumnIds={tableState.visibleColumnIds}
            setColumnVisibility={tableState.setColumnVisibility}
          />
        </DataTable.Actions>
        <DataTable.ActiveFilters
          {...tableState.activeFilters}
          filterDefinitions={filterDefinitions}
        />
        <DataTable.Table<DatasetRow>
          {...tableState.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No datasets"
          totalRowCount={countQuery.data}
          selectionEnabled
        />
      </DataTable.Root>
    </PageLayout>
  );
}

function CreatedByCell(params: ICellRendererParams<DatasetRow, string>) {
  const value = params.value ?? "";
  const principal = RobotoPrincipal.fromString(value);
  if (principal?.ptype === RobotoPrincipalType.User) {
    return (
      <UserLink
        userId={principal.unqualifiedId}
        linkSx={{ fontSize: "var(--font-size-xs)" }}
      />
    );
  }
  return <span>{principal?.unqualifiedId ?? value}</span>;
}
